import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * Normalize variant option values and media alt text to the same lookup key.
 * Example: "Olive Grove" and "olivegrove" both become "olivegrove".
 *
 * @param {unknown} value
 * @returns {string}
 */
function normalizeVariantMediaKey(value) {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Get the selected variant option values from the variant picker rendered with the gallery.
 *
 * @param {ParentNode} root
 * @returns {string[]}
 */
function getSelectedVariantOptionKeys(root) {
  const picker =
    root.querySelector('variant-picker[data-template-product-match="true"]') ?? root.querySelector('variant-picker');
  const variantScript = picker?.querySelector('script[type="application/json"]');

  if (!variantScript?.textContent) return [];

  try {
    const variant = JSON.parse(variantScript.textContent);
    const optionValues =
      Array.isArray(variant.options) && variant.options.length
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter(Boolean);

    return optionValues.map(normalizeVariantMediaKey).filter(Boolean);
  } catch (error) {
    console.warn('Unable to read selected variant for media filtering.', error);
    return [];
  }
}

/**
 * Read the media alt text used as the variant-media key.
 *
 * @param {Element} element
 * @returns {string}
 */
function getMediaAltKey(element) {
  const image = element.querySelector('.product-media img[alt], img[alt]');
  return normalizeVariantMediaKey(image?.getAttribute('alt'));
}

/**
 * Rewrite theme action attributes after media nodes have been removed so indexes stay aligned.
 *
 * @param {Element} root
 * @param {number} index
 */
function reindexActions(root, index) {
  const elements = [root, ...root.querySelectorAll('*')];

  for (const element of elements) {
    for (const attribute of ['on:click', 'on:pointerenter']) {
      const value = element.getAttribute(attribute);
      if (!value || !/\/\d+$/.test(value)) continue;
      element.setAttribute(attribute, value.replace(/\/\d+$/, `/${index}`));
    }
  }
}

/**
 * Remove entries that do not belong to the selected variant while keeping their relative order.
 *
 * @param {Element[]} elements
 * @param {Set<number>} keepIndexes
 * @returns {Element[]}
 */
function filterIndexedElements(elements, keepIndexes) {
  const kept = [];

  elements.forEach((element, index) => {
    if (!keepIndexes.has(index)) {
      element.remove();
      return;
    }

    kept.push(element);
  });

  kept.forEach((element, index) => reindexActions(element, index));
  return kept;
}

/**
 * Filter a media gallery by matching selected variant option values to media alt text.
 * If there is no matching alt mapping, the gallery is left completely untouched as a fallback.
 *
 * @param {Element} gallery
 * @param {ParentNode} variantRoot
 * @returns {boolean} Whether variant filtering was applied.
 */
function filterGalleryByVariantAlt(gallery, variantRoot) {
  const optionKeys = getSelectedVariantOptionKeys(variantRoot);
  if (!optionKeys.length) return false;

  const slides = [...gallery.querySelectorAll('slideshow-component[ref="slideshow"] slideshow-slide[ref="slides[]"]')];
  if (!slides.length) return false;

  const slideAltKeys = slides.map(getMediaAltKey);
  const availableAltKeys = new Set(slideAltKeys.filter(Boolean));
  const variantMediaKey = optionKeys.find((key) => availableAltKeys.has(key));

  // Fallback: no alt text has been mapped to the selected variant, so render the normal full gallery.
  if (!variantMediaKey) return false;

  const keepIndexes = new Set();
  slideAltKeys.forEach((key, index) => {
    if (key === variantMediaKey) keepIndexes.add(index);
  });

  if (!keepIndexes.size) return false;

  const filteredSlides = filterIndexedElements(slides, keepIndexes);
  const mediaCount = filteredSlides.length;

  filteredSlides.forEach((slide, index) => {
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

    const style = slide.getAttribute('style');
    if (style) slide.setAttribute('style', style.replace(/--slide-\d+/g, `--slide-${index}`));
  });

  const gridItems = [...gallery.querySelectorAll('.media-gallery__grid > li')];
  if (gridItems.length) filterIndexedElements(gridItems, keepIndexes);

  const zoomItems = [...gallery.querySelectorAll('.dialog-zoomed-gallery > li')];
  if (zoomItems.length) filterIndexedElements(zoomItems, keepIndexes);

  const zoomThumbnails = [...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
  if (zoomThumbnails.length) {
    const filteredZoomThumbnails = filterIndexedElements(zoomThumbnails, keepIndexes);
    filteredZoomThumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    });
  }

  for (const controls of gallery.querySelectorAll('slideshow-controls')) {
    const thumbnails = [...controls.querySelectorAll('.slideshow-controls__thumbnail')];
    if (thumbnails.length) {
      const filteredThumbnails = filterIndexedElements(thumbnails, keepIndexes);
      filteredThumbnails.forEach((thumbnail, index) => {
        thumbnail.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      });
    }

    const dots = [...controls.querySelectorAll('.slideshow-controls__dots > li')];
    if (dots.length) {
      const filteredDots = filterIndexedElements(dots, keepIndexes);
      filteredDots.forEach((dot, index) => {
        dot.querySelector('button')?.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
      });
    }

    const counter = controls.querySelector('.slideshow-controls__counter');
    if (counter) {
      counter.innerHTML = `<span ref="current">1</span><span class="slash">/</span>${mediaCount}`;
    }
  }

  const slideshow = gallery.querySelector('slideshow-component[ref="slideshow"]');
  if (slideshow) {
    slideshow.setAttribute('initial-slide', '0');

    const timeline = Array.from({ length: mediaCount }, (_, index) => `--slide-${index}`).join(',');
    const style = slideshow.getAttribute('style') ?? '';
    const updatedStyle = /--slideshow-timeline:[^;]*;/.test(style)
      ? style.replace(/--slideshow-timeline:[^;]*;/, `--slideshow-timeline: ${timeline};`)
      : `--slideshow-timeline: ${timeline};${style}`;
    slideshow.setAttribute('style', updatedStyle);

    if (mediaCount <= 1) {
      slideshow.classList.add('product-media-gallery__slideshow--single-media', 'slideshow--single-media');
      slideshow.querySelectorAll('slideshow-controls, slideshow-arrows').forEach((element) => element.remove());
      gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach((element) => element.remove());
    }
  }

  gallery.dataset.variantMediaFiltered = variantMediaKey;
  return true;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    // Filter a detached clone on first load so child slideshow components initialize
    // against the already-filtered DOM rather than being mutated after initialization.
    if (!this.dataset.variantMediaReady) {
      const variantRoot = this.closest('.shopify-section') ?? document;
      const clone = this.cloneNode(true);

      if (filterGalleryByVariantAlt(clone, variantRoot)) {
        clone.dataset.variantMediaReady = 'true';
        this.replaceWith(clone);
        return;
      }

      this.dataset.variantMediaReady = 'true';
    }

    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    filterGalleryByVariantAlt(newMediaGallery, source);
    newMediaGallery.dataset.variantMediaReady = 'true';
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
